import React, { useState, useEffect } from 'react';
import { getLegacyApiUrl } from '../utils/apiUtils';
import { useNavigate } from 'react-router-dom';
import LecturerSidebar from '../components/LecturerSidebar';
import { useQRSession } from '../contexts/QRSessionContext';
import { useAuth } from '../contexts/AuthContext';

const QRConfig = () => {
  const navigate = useNavigate();
  const { createSession, startQRGeneration, loading, error } = useQRSession();
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    semester: '',
    year: '', // Changed from level to year
    session_type: '',
    module_code: '',
    groups: [], // Changed to array for multi-select
    location: '',
    date: '',
    class_start_time: '', // New field
    class_end_time: '', // New field
    description: '', // New field
    academic_year: '', // New field
    assistant_lecturer: ''
  });

  // State for dropdown options
  const [semesters, setSemesters] = useState([]);
  const [modules, setModules] = useState([]);
  const [assistantLecturers, setAssistantLecturers] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingAssistants, setLoadingAssistants] = useState(false);

  // Fetch initial data when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingData(true);
        
        // Use actual lecturer ID from our test data or current user
        // For testing, using the lecturer ID we know has staff_courses data
        const lecturerId = currentUser?.id || '149d0cc7-0ba2-4761-993c-4b8763f04411';
        
        console.log('Fetching data for lecturer ID:', lecturerId);
        
        // Fetch lecturer's semesters
        const semesterResponse = await fetch(`${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/semesters`);
        const semesterData = await semesterResponse.json();
        
        console.log('Semester API response:', semesterData);
        
        if (semesterData.success) {
          setSemesters(semesterData.semesters);
        } else {
          console.error('Failed to fetch semesters:', semesterData.error);
        }
        
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchInitialData();
  }, [currentUser]);

  // Fetch modules when semester changes
  useEffect(() => {
    const fetchModules = async () => {
      if (formData.semester) {
        try {
          const lecturerId = currentUser?.id || '149d0cc7-0ba2-4761-993c-4b8763f04411';
          console.log('Fetching modules for lecturer:', lecturerId, 'semester:', formData.semester);
          
          const response = await fetch(`${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/modules/${formData.semester}`);
          const data = await response.json();
          
          console.log('Modules API response:', data);
          
          if (data.success) {
            setModules(data.modules);
          } else {
            console.error('Failed to fetch modules:', data.error);
            setModules([]);
          }
        } catch (error) {
          console.error('Error fetching modules:', error);
          setModules([]);
        }
      } else {
        setModules([]);
      }
    };

    fetchModules();
  }, [formData.semester, currentUser]);

  // Fetch groups and assistant lecturers when module changes
  useEffect(() => {
    const fetchModuleData = async () => {
      if (formData.module_code) {
        try {
          // Fetch groups
          setLoadingGroups(true);
          console.log('Fetching groups for module:', formData.module_code, 'with filters:', {
            semester: formData.semester,
            year: formData.year,
            academic_year: formData.academic_year
          });
          
          // Build query parameters for enhanced filtering
          const queryParams = new URLSearchParams();
          if (formData.semester) queryParams.append('semester', formData.semester);
          if (formData.year) queryParams.append('year', formData.year);
          if (formData.academic_year) queryParams.append('academic_year', formData.academic_year);
          
          // Get lecturer ID for API call
          const lecturerId = currentUser?.id || '149d0cc7-0ba2-4761-993c-4b8763f04411';
          
          const queryString = queryParams.toString();
          const groupsUrl = `${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/module/${formData.module_code}/groups${queryString ? `?${queryString}` : ''}`;
          
          console.log('Groups API URL:', groupsUrl);
          const groupsResponse = await fetch(groupsUrl);
          const groupsData = await groupsResponse.json();
          
          console.log('Groups API response:', groupsData);
          
          if (groupsData.success) {
            setAvailableGroups(groupsData.groups);
          } else {
            console.error('Failed to fetch groups:', groupsData.error);
            setAvailableGroups([]);
          }

          // Fetch assistant lecturers
          setLoadingAssistants(true);
          console.log('Fetching assistant lecturers for lecturer:', lecturerId, 'module:', formData.module_code);
          
          const assistantResponse = await fetch(`${getLegacyApiUrl()}/qr-sessions/dropdown/lecturer/${lecturerId}/module/${formData.module_code}/assistants`);
          const assistantData = await assistantResponse.json();
          
          console.log('Assistant lecturers API response:', assistantData);
          
          if (assistantData.success) {
            setAssistantLecturers(assistantData.assistants);
          } else {
            console.error('Failed to fetch assistant lecturers:', assistantData.error);
            setAssistantLecturers([]);
          }
          
        } catch (error) {
          console.error('Error fetching module data:', error);
          setAvailableGroups([]);
          setAssistantLecturers([]);
        } finally {
          setLoadingGroups(false);
          setLoadingAssistants(false);
        }
      } else {
        setAvailableGroups([]);
        setAssistantLecturers([]);
      }
    };

    fetchModuleData();
  }, [formData.module_code, formData.semester, formData.year, formData.academic_year]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset module_code when semester changes
    if (name === 'semester') {
      setFormData(prev => ({
        ...prev,
        module_code: '',
        groups: [] // Reset groups when semester changes
      }));
    }

    // Reset groups and assistant lecturer when module changes
    if (name === 'module_code') {
      setFormData(prev => ({
        ...prev,
        groups: [], // Reset groups when module changes
        assistant_lecturer: '' // Reset assistant lecturer when module changes
      }));
    }
  };

  const handleGroupToggle = (groupId) => {
    setFormData(prev => ({
      ...prev,
      groups: prev.groups.includes(groupId)
        ? prev.groups.filter(id => id !== groupId)
        : [...prev.groups, groupId]
    }));
  };

  const handleGenerateQR = async () => {
    try {
      // Validate required fields
      const requiredFields = ['semester', 'year', 'session_type', 'module_code', 'date', 'class_start_time', 'class_end_time', 'academic_year'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        alert(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Validate time logic
      if (formData.class_start_time >= formData.class_end_time) {
        alert('Class end time must be after class start time');
        return;
      }

      console.log('Starting QR generation process...');

      // Create session
      const sessionData = {
        ...formData,
        lecturer_id: currentUser?.id, // Add lecturer_id from current user
        // Combine date and class_start_time for session_datetime
        session_datetime: `${formData.date}T${formData.class_start_time}:00`,
        module_name: formData.module_code // You might want to have a separate module name field
      };

      // Validate that we have a lecturer_id
      if (!sessionData.lecturer_id) {
        alert('Unable to identify lecturer. Please log in again.');
        return;
      }

      console.log('Creating session with data:', sessionData);
      const session = await createSession(sessionData);
      console.log('Session created:', session);
      
      // Start QR generation immediately
      console.log('Starting QR generation for session:', session.session_id);
      const qrData = await startQRGeneration(session.session_id);
      console.log('QR generation started:', qrData);
      
      // Navigate to QR display page with session data
      navigate('/qr-display', { 
        state: { 
          session: session,
          qrStarted: true
        } 
      });
      
    } catch (err) {
      console.error('Error generating QR:', err);
      alert(`Failed to generate QR code: ${err.message}`);
    }
  };
  
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F6FA' }}>
        <LecturerSidebar />

        {/* Main Content */}
        <div style={{ marginLeft: 290, padding: '32px 40px', flex: 1, display: 'flex' }}>
        {/* QR Code Illustration */}
        <div style={{ 
          flex: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingRight: '60px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '600px',
            height: '600px',
            position: 'relative'
          }}>
            <img 
              src="/assets/qrscreen.png" 
              alt="QR Screen" 
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }} 
            />
          </div>
        </div>

        {/* Form Section */}
        <div style={{ flex: 0.8, paddingLeft: 40 }}>
          {loadingData && (
            <div style={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              padding: '12px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '1px solid #bbdefb',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              ⟲ Loading lecturer data...
            </div>
          )}
          
          {error && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '12px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '1px solid #f5c6cb'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
            {/* Semester */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Semester *</label>
              <div style={{ position: 'relative' }}>
                <select
                  name="semester"
                  value={formData.semester}
                  onChange={handleInputChange}
                  disabled={loadingData}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: loadingData ? 'not-allowed' : 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select semester</option>
                  {semesters.map((semester, index) => (
                    <option key={index} value={semester.value}>{semester.label}</option>
                  ))}
                </select>
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="#252C58"
                >
                  <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            {/* Year (changed from Level) */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Year *</label>
              <select
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  height: 40,
                  border: '1px solid #D5D9DD',
                  borderRadius: 5,
                  padding: '0 16px',
                  fontSize: 14,
                  color: '#252C58',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
              >
                <option value="">Select year</option>
                <option value="1">Year 1</option>
                <option value="2">Year 2</option>
                <option value="3">Year 3</option>
                <option value="4">Year 4</option>
              </select>
              <svg 
                style={{ 
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
                width="10" 
                height="6" 
                viewBox="0 0 10 6" 
                fill="#252C58"
              >
                <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Session Type */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Session Type *</label>
              <div style={{ position: 'relative' }}>
                <select
                  name="session_type"
                  value={formData.session_type}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select session type</option>
                  <option value="lecture">Lecture</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="practical">Practical</option>
                  <option value="laboratory">Laboratory</option>
                  <option value="workshop">Workshop</option>
                </select>
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="#252C58"
                >
                  <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            {/* Module Code */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Module Code *</label>
              <div style={{ position: 'relative' }}>
                <select
                  name="module_code"
                  value={formData.module_code}
                  onChange={handleInputChange}
                  disabled={!formData.semester || modules.length === 0}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: (!formData.semester || modules.length === 0) ? 'not-allowed' : 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">
                    {!formData.semester 
                      ? "Select semester first" 
                      : modules.length === 0 
                        ? "No modules available" 
                        : "Select module code"
                    }
                  </option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.code}>
                      {module.code} - {module.name}
                    </option>
                  ))}
                </select>
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="#252C58"
                >
                  <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            {/* Academic Year */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Academic Year *</label>
              <select
                name="academic_year"
                value={formData.academic_year}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  height: 40,
                  border: '1px solid #D5D9DD',
                  borderRadius: 5,
                  padding: '0 16px',
                  fontSize: 14,
                  color: '#252C58',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
              >
                <option value="">Select academic year</option>
                <option value="2023-2024">2023-2024</option>
                <option value="2024-2025">2024-2025</option>
                <option value="2025-2026">2025-2026</option>
                <option value="2026-2027">2026-2027</option>
                <option value="2027-2028">2027-2028</option>
                <option value="2028-2029">2028-2029</option>
                <option value="2029-2030">2029-2030</option>
              </select>
              <svg 
                style={{ 
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
                width="10" 
                height="6" 
                viewBox="0 0 10 6" 
                fill="#252C58"
              >
                <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Location */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Location</label>
              <div style={{ position: 'relative' }}>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none'
                  }}
                >
                  <option value="">Select country</option>
                  <option value="LK">Sri Lanka</option>
                  <option value="UK">United Kingdom</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="IN">India</option>
                  <option value="MY">Malaysia</option>
                  <option value="SG">Singapore</option>
                  <option value="ID">Indonesia</option>
                  <option value="TH">Thailand</option>
                  <option value="VN">Vietnam</option>
                  <option value="PH">Philippines</option>
                </select>
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="#252C58"
                >
                  <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            {/* Date */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Date *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    cursor: 'pointer'
                  }}
                />
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path d="M19 4H5C3.89 4 3 4.9 3 6V20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM7 12H17V14H7V12ZM7 16H14V18H7V16Z" fill="#252C58"/>
                </svg>
              </div>
            </div>

            {/* Class Start Time */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Class Start Time *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="time"
                  name="class_start_time"
                  value={formData.class_start_time}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    cursor: 'pointer'
                  }}
                />
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="#252C58"/>
                </svg>
              </div>
            </div>

            {/* Class End Time */}
            <div>
              <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Class End Time *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="time"
                  name="class_end_time"
                  value={formData.class_end_time}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    height: 40,
                    border: '1px solid #D5D9DD',
                    borderRadius: 5,
                    padding: '0 16px',
                    fontSize: 14,
                    color: '#252C58',
                    cursor: 'pointer'
                  }}
                />
                <svg 
                  style={{ 
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                  }}
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="#252C58"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Groups Multi-select */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>
              Groups {loadingGroups && <span style={{ color: '#1976d2' }}>(Loading...)</span>}
            </label>
            <div style={{
              border: '1px solid #D5D9DD',
              borderRadius: 5,
              minHeight: 40,
              maxHeight: 120,
              overflowY: 'auto',
              padding: 8,
              backgroundColor: 'white'
            }}>
              {!formData.module_code ? (
                <div style={{ padding: 8, color: '#666' }}>Select a module first</div>
              ) : availableGroups.length === 0 ? (
                <div style={{ padding: 8, color: '#666' }}>
                  {loadingGroups ? 'Loading groups...' : 'No groups available for this module'}
                </div>
              ) : (
                availableGroups.map((group) => (
                  <div key={group.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 8px',
                    margin: '2px 0',
                    borderRadius: 3,
                    cursor: 'pointer',
                    backgroundColor: formData.groups.includes(group.id) ? '#e3f2fd' : 'transparent',
                    border: formData.groups.includes(group.id) ? '1px solid #1976d2' : '1px solid transparent'
                  }}
                  onClick={() => handleGroupToggle(group.id)}
                  >
                    <input
                      type="checkbox"
                      checked={formData.groups.includes(group.id)}
                      onChange={() => handleGroupToggle(group.id)}
                      style={{ marginRight: 8 }}
                    />
                    <span>{group.name} (Year {group.year})</span>
                  </div>
                ))
              )}
            </div>
            {formData.groups.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                {formData.groups.length} group(s) selected
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe what was taught in this session..."
              style={{
                width: '100%',
                minHeight: 80,
                border: '1px solid #D5D9DD',
                borderRadius: 5,
                padding: 16,
                fontSize: 14,
                color: '#252C58',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Assistant Lecturer */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: 14, color: '#252C58', marginBottom: 8, display: 'block' }}>
              Assistant Lecturer {loadingAssistants && <span style={{ color: '#1976d2' }}>(Loading...)</span>}
            </label>
            <div style={{ position: 'relative' }}>
              <select
                name="assistant_lecturer"
                value={formData.assistant_lecturer}
                onChange={handleInputChange}
                disabled={!formData.module_code || loadingAssistants || assistantLecturers.length === 0}
                style={{
                  width: '100%',
                  height: 40,
                  border: '1px solid #D5D9DD',
                  borderRadius: 5,
                  padding: '0 16px',
                  fontSize: 14,
                  color: '#252C58',
                  backgroundColor: 'white',
                  cursor: (!formData.module_code || loadingAssistants || assistantLecturers.length === 0) ? 'not-allowed' : 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
              >
                <option value="">
                  {!formData.module_code 
                    ? "Select module first" 
                    : loadingAssistants 
                      ? "Loading assistant lecturers..." 
                      : assistantLecturers.length === 0 
                        ? "No qualified assistant lecturers available" 
                        : "Select Assistant Lecturer"
                  }
                </option>
                {assistantLecturers.map((lecturer) => (
                  <option key={lecturer.id} value={lecturer.id}>
                    {lecturer.name} ({lecturer.employee_id}) - {lecturer.staff_type}
                  </option>
                ))}
              </select>
              <svg 
                style={{ 
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
                width="10" 
                height="6" 
                viewBox="0 0 10 6" 
                fill="#252C58"
              >
                <path d="M1 1L5 5L9 1" stroke="#252C58" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            {formData.module_code && assistantLecturers.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                Showing only lecturers qualified to assist with this module
              </div>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
            <button 
              onClick={handleBack}
              style={{
                padding: '8px 24px',
                border: '1px solid #F04915',
                borderRadius: 5,
                background: 'white',
                color: '#F04915',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button 
              onClick={() => navigate('/manual-attendance-mark')}
              style={{
                padding: '8px 24px',
                border: '1px solid #252C58',
                borderRadius: 5,
                background: 'white',
                color: '#252C58',
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#252C58">
                <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8.82 13.05L7.4 14.46L10.94 18L16.6 12.34L15.19 10.93L10.94 15.17L8.82 13.05Z"/>
              </svg>
              Manual Mark
            </button>
            <button 
              onClick={handleGenerateQR}
              disabled={loading}
              style={{
                padding: '8px 24px',
                border: 'none',
                borderRadius: 5,
                background: loading ? '#ccc' : '#F04915',
                color: 'white',
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M3 11H11V3H3V11ZM5 5H9V9H5V5Z"/>
                <path d="M3 21H11V13H3V21ZM5 15H9V19H5V15Z"/>
                <path d="M13 21H21V13H13V21ZM15 15H19V19H15V15Z"/>
                <path d="M13 3V11H21V3H13ZM19 9H15V5H19V9Z"/>
                <path d="M7 7H7.01V7.01H7z"/>
                <path d="M7 17H7.01V17.01H7z"/>
                <path d="M17 7H17.01V7.01H17z"/>
                <path d="M17 17H17.01V17.01H17z"/>
              </svg>
              {loading ? 'Generating...' : 'Generate QR'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRConfig;